import { useMemo } from 'react'
import { Button } from '@/components/ui/button'
import { MultiSelectCombobox, ComboboxOption } from '@/components/ui/combobox'
import { RotateCcw } from 'lucide-react'

export interface ColumnConfig {
  key: string
  label: string
  visible: boolean
  required?: boolean // Some columns like "Name" might be required
  align?: 'left' | 'center' | 'right' // Column alignment for header and cells
  hasData?: boolean // Whether this column has any non-null data
}

interface ColumnVisibilityControlProps {
  columns: ColumnConfig[]
  onVisibilityChange: (columnKey: string, visible: boolean) => void
  onResetToDefaults?: () => void
}

export function ColumnVisibilityControl({
  columns,
  onVisibilityChange,
  onResetToDefaults
}: ColumnVisibilityControlProps) {
  // Convert columns to ComboboxOption format
  const options: ComboboxOption[] = useMemo(() => {
    return columns.map(col => {
      const hasNoData = col.hasData === false
      const isDisabled = col.required || hasNoData

      let label = col.label
      if (col.required) {
        label += ' (Obligatorisk)'
      } else if (hasNoData) {
        label += ' (Ingen data)'
      }

      return {
        value: col.key,
        label,
        disabled: isDisabled
      }
    })
  }, [columns])

  // Get currently visible column keys (excluding columns with no data)
  const visibleKeys = useMemo(() => {
    return columns
      .filter(col => col.visible && col.hasData !== false)
      .map(col => col.key)
  }, [columns])

  // Handle selection changes
  const handleValuesChange = (newKeys: string[]) => {
    // For each column, update visibility based on whether it's in newKeys
    columns.forEach(col => {
      const shouldBeVisible = newKeys.includes(col.key)
      if (col.visible !== shouldBeVisible && !col.required && col.hasData !== false) {
        onVisibilityChange(col.key, shouldBeVisible)
      }
    })
  }

  return (
    <div className="flex items-center gap-2">
      <MultiSelectCombobox
        options={options}
        values={visibleKeys}
        onValuesChange={handleValuesChange}
        placeholder="Välj kolumner..."
        searchPlaceholder="Sök kolumn..."
        emptyMessage="Ingen kolumn hittades."
        className="w-[200px]"
        showSelectAll={true}
        getDisplayText={(selected, total) => `Kolumner (${selected}/${total})`}
      />
      {onResetToDefaults && (
        <Button variant="outline" size="icon" onClick={onResetToDefaults} title="Återställ till standard">
          <RotateCcw className="h-4 w-4" />
        </Button>
      )}
    </div>
  )
}